import {
  Button,
  LineColorPickerWithNames,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { useDisplayMarks } from '@ais-ui/websockets';
import { baseColours } from '@ais-ui/react-openlayers';
import { useState } from 'react';
import { PiMapPinFill } from 'react-icons/pi';

const hslToHex = (hsl: string): string => {
  const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) return 'FFCC99';

  const h = parseInt(match[1]) / 360;
  const s = parseInt(match[2]) / 100;
  const l = parseInt(match[3]) / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [r, g, b]
    .map((x) =>
      Math.round(x * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')
    .toUpperCase();
};

interface MarkTargetWithColorProps {
  onSuccess: () => void;
}

export const MarkTargetWithColor = ({
  onSuccess,
}: MarkTargetWithColorProps) => {
  const [markColor, setMarkColor] = useState<string>('blue');
  const { toast } = useToast();
  const { markTarget } = useDisplayMarks();

  const onMarkTargetClick = async () => {
    try {
      const hslColor = baseColours[markColor as keyof typeof baseColours];
      const hexColor = hslToHex(hslColor);
      await markTarget(hexColor);
      toast({
        title: 'Success',
        description: 'Target marked successfully',
        variant: 'default',
      });
      onSuccess();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to mark target',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="col-span-2 flex items-center gap-2">
      <Button
        id="mark-target-button"
        variant="ghost"
        onClick={onMarkTargetClick}
        className="flex gap-x-2 col-span-2 justify-start"
      >
        <PiMapPinFill />
        Mark Target
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <button
            id="mark-color-picker"
            className="h-6 w-6 rounded-md border border-input transition-all hover:scale-105"
            style={{
              backgroundColor:
                baseColours[markColor as keyof typeof baseColours],
            }}
          />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" side="right">
          <LineColorPickerWithNames
            background={markColor}
            setBackground={setMarkColor}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
